const getStringValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return `${value}`.trim();
};

export const DEPARTMENT_LABELS = {
  financeDepartment: "Finance department",
  technicalDepartment: "Technical department",
  humanResourcesDepartment: "Human Resources department",
  waterDepartment: "Water department",
  sewerageDepartment: "Sewerage department",
  communityDepartment: "Community department",
  cleansingDepartment: "Cleansing department",
  corporateServicesDepartment: "Corporate services department",
  mechanicalDepartment: "Mechanical department",
  parksAndRecreationDepartment: "Parks and recreation department",
  localAndEconomicDevelopmentDepartment:
    "Local and Economic development department",
  publicRelationsDepartment: "Public relations department",
  councilDepartment: "Council department",
};

export const REPORT_HEADERS = {
  executiveSummary: "Executive Summary",
};

export const ADJUSTMENT_SCOPES = {
  wholeBudget: "wholeBudget",
  department: "department",
  wholeDepartment: "wholeDepartment",
  singleLineItem: "singleLineItem",
};

export const toAbsoluteInteger = (value) => {
  const normalizedValue = getStringValue(value).replace(/,/g, "");
  if (!normalizedValue) {
    return 0;
  }

  const parsedValue = Number.parseFloat(normalizedValue);
  if (Number.isNaN(parsedValue)) {
    return 0;
  }

  return Math.abs(Math.round(parsedValue));
};

export const normalizeWorksheetRows = (sheetRows = []) => {
  const headerRowIndex = sheetRows.findIndex((row = []) =>
    row.some((cell) => getStringValue(cell)),
  );

  if (headerRowIndex < 0) {
    return { headers: [], rows: [] };
  }

  const rawHeaders = sheetRows[headerRowIndex] ?? [];
  const headers = rawHeaders.map((header, index) => {
    const trimmedHeader = getStringValue(header);
    return trimmedHeader || `Column ${index + 1}`;
  });

  const rows = sheetRows
    .slice(headerRowIndex + 1)
    .filter((row = []) => row.some((cell) => getStringValue(cell)))
    .map((row = []) =>
      headers.reduce((agg, header, index) => {
        agg[header] = row[index];
        return agg;
      }, {}),
    );

  return { headers, rows };
};

export const buildImportedBudgetRows = ({
  rows,
  mapping,
}) => {
  return rows.reduce((agg, row, index) => {
    const lineItem = getStringValue(row?.[mapping.lineItem]);
    const actualPriorYear = mapping.actualPriorYear
      ? toAbsoluteInteger(row?.[mapping.actualPriorYear])
      : 0;

    if (!lineItem) {
      return agg;
    }

    agg.push({
      id: `imported-${index + 1}`,
      sourceLineItem: lineItem || `Line item ${index + 1}`,
      estimatedCurrentYear: 0,
      estimatedPriorYear: 0,
      actualPriorYear,
      viCode: "",
      viDescription: "",
      codeDescription: lineItem || `Line item ${index + 1}`,
    });

    return agg;
  }, []);
};

export const buildViOptions = (vicodeList = []) => {
  return (vicodeList ?? []).map((item) => ({
    value: `${item.code}`,
    label: `${item.code} - ${item.name}`,
    code: item.code,
    name: item.name,
    departments: Array.isArray(item.departments) ? item.departments : [],
    type: item.type ?? "",
  }));
};

export const buildAccountingMatchMap = (yearFinancials = []) => {
  return (yearFinancials ?? []).reduce((agg, item) => {
    const candidates = [
      getStringValue(item?.codeDescription).toLowerCase(),
      getStringValue(item?.viDescription).toLowerCase(),
    ].filter(Boolean);

    candidates.forEach((candidate) => {
      if (!agg[candidate] && item?.viCode) {
        agg[candidate] = {
          viCode: `${item.viCode}`,
          viDescription:
            getStringValue(item?.viDescription) ||
            getStringValue(item?.codeDescription),
        };
      }
    });

    return agg;
  }, {});
};

export const applyAutoMatchToImportedRows = (importedRows = [], matchMap = {}) => {
  return (importedRows ?? []).map((row) => {
    const matchKey = getStringValue(row?.sourceLineItem).toLowerCase();
    const matchedRow = matchMap[matchKey];

    if (!matchedRow || row?.viCode) {
      return row;
    }

    return {
      ...row,
      viCode: matchedRow.viCode,
      viDescription: matchedRow.viDescription,
    };
  });
};

export const applyExcelToViMappingToImportedRows = (
  importedRows = [],
  excelToViMapping = {},
) => {
  return (importedRows ?? []).map((row) => {
    const matchKey = getStringValue(
      row?.codeDescription || row?.sourceLineItem,
    ).toLowerCase();
    const matchedRow = excelToViMapping?.[matchKey];

    if (!matchedRow || row?.viCode || row?.viDescription) {
      return row;
    }

    return {
      ...row,
      viCode: `${matchedRow.viCode ?? ""}`,
      viDescription: getStringValue(matchedRow.viDescription),
    };
  });
};

export const buildExcelToViMappingPatch = (rows = []) => {
  return (rows ?? []).reduce((agg, row) => {
    const excelDescription = getStringValue(
      row?.codeDescription || row?.sourceLineItem,
    ).toLowerCase();
    const viCode = getStringValue(row?.viCode);
    const viDescription = getStringValue(row?.viDescription);

    if (excelDescription && viCode && viDescription) {
      agg[excelDescription] = {
        viCode,
        viDescription,
      };
    }

    return agg;
  }, {});
};

export const buildMappedBudgetRows = (importedRows = [], viOptions = []) => {
  const viMap = viOptions.reduce((agg, option) => {
    agg[`${option.code}`] = option.name;
    return agg;
  }, {});

  const groupedRows = importedRows.reduce((agg, row) => {
    const viCode = getStringValue(row?.viCode);

    if (!viCode) {
      return agg;
    }

    if (!agg[viCode]) {
      agg[viCode] = {
        viCode,
        viDescription: row?.viDescription || viMap[viCode] || "",
        codeDescription: row?.viDescription || viMap[viCode] || "",
        departments:
          viOptions.find((option) => `${option.code}` === viCode)?.departments ?? [],
        category:
          viOptions.find((option) => `${option.code}` === viCode)?.type ?? "",
        estimatedCurrentYear: 0,
        estimatedPriorYear: 0,
        actualPriorYear: 0,
        sourceLineItems: [],
      };
    }

    agg[viCode].estimatedCurrentYear += toAbsoluteInteger(
      row?.estimatedCurrentYear,
    );
    agg[viCode].estimatedPriorYear += toAbsoluteInteger(row?.estimatedPriorYear);
    agg[viCode].actualPriorYear += toAbsoluteInteger(row?.actualPriorYear);
    agg[viCode].sourceLineItems.push(row?.sourceLineItem || "");

    return agg;
  }, {});

  return Object.values(groupedRows).sort((firstItem, secondItem) => {
    return `${firstItem.viDescription}`.localeCompare(`${secondItem.viDescription}`);
  });
};

const normalizeDepartmentKeys = (departments = []) => {
  return Array.isArray(departments)
    ? departments.filter((department) => DEPARTMENT_LABELS[department])
    : [];
};

export const enrichBudgetRowsWithReferenceData = (
  budgetRows = [],
  vicodeList = [],
) => {
  const referenceMap = buildViOptions(vicodeList).reduce((agg, option) => {
    agg[`${option.code}`] = option;
    return agg;
  }, {});

  return (budgetRows ?? []).map((row) => {
    const reference = referenceMap[`${row?.viCode}`] ?? {};
    const rowDepartments = normalizeDepartmentKeys(row?.departments);
    const referenceDepartments = normalizeDepartmentKeys(reference.departments);

    return {
      ...row,
      departments:
        rowDepartments.length > 0 ? rowDepartments : referenceDepartments,
      category: row?.category || reference.type || "",
    };
  });
};

export const buildActualOutcomeMap = (
  yearFinancials = [],
  fallbackRows = [],
) => {
  const financialRows = Array.isArray(yearFinancials) ? yearFinancials : [];

  if (financialRows.length > 0) {
    return financialRows.reduce((agg, row) => {
      const viCode = getStringValue(row?.viCode);
      if (viCode) {
        const derivedAmount =
          row?.total !== undefined && row?.total !== null
            ? row.total
            : Math.abs((Number(row?.credit) || 0) - (Number(row?.debit) || 0));
        agg[viCode] = toAbsoluteInteger(derivedAmount);
      }
      return agg;
    }, {});
  }

  return (fallbackRows ?? []).reduce((agg, row) => {
    const viCode = getStringValue(row?.viCode);
    if (viCode) {
      agg[viCode] = toAbsoluteInteger(row?.actualPriorYear);
    }
    return agg;
  }, {});
};

export const buildPreviousBudgetMap = (budgetRows = []) => {
  return (Array.isArray(budgetRows) ? budgetRows : []).reduce((agg, row) => {
    const viCode = getStringValue(row?.viCode);
    if (viCode) {
      agg[viCode] = toAbsoluteInteger(row?.estimatedCurrentYear);
    }
    return agg;
  }, {});
};

export const getPreviousFinancialYear = (financialYear = "") => {
  const [startYear] = `${financialYear}`.split("-");
  const parsedStartYear = Number.parseInt(startYear, 10);

  if (Number.isNaN(parsedStartYear)) {
    return "";
  }

  return `${parsedStartYear - 1}-${parsedStartYear}`;
};

export const buildPlannerHeaderLabels = (
  financialYear = "",
  actualOutcomeFinancialYear = "",
) => {
  const previousFinancialYear = getPreviousFinancialYear(financialYear);
  const actualFinancialYear = actualOutcomeFinancialYear || previousFinancialYear;

  return {
    previousBudget: {
      title: "Budget",
      year: previousFinancialYear ? `(${previousFinancialYear})` : "(prev FY)",
    },
    actualOutcome: {
      title: "Actual Outcome",
      year: actualFinancialYear ? `(${actualFinancialYear})` : "(prev FY)",
    },
    estimated: {
      title: "Estimated Budget",
      year: financialYear ? `(${financialYear})` : "(current FY)",
    },
  };
};

export const buildPlannerWorkspaceRows = (
  budgetRows = [],
  vicodeList = [],
) => {
  const currentRows = Array.isArray(budgetRows) ? budgetRows : [];
  const rowMap = currentRows.reduce((agg, row) => {
    const viCode = getStringValue(row?.viCode);
    if (viCode) {
      agg[viCode] = row;
    }
    return agg;
  }, {});
  const mergedRows = [];
  const usedCodes = new Set();

  buildViOptions(vicodeList).forEach((option) => {
    const viCode = `${option.code}`;
    const currentRow = rowMap[viCode] ?? {};
    const currentDepartments = normalizeDepartmentKeys(currentRow?.departments);
    const referenceDepartments = normalizeDepartmentKeys(option.departments);

    mergedRows.push({
      ...currentRow,
      viCode,
      viDescription: currentRow?.viDescription || option.name || "",
      codeDescription:
        currentRow?.codeDescription ||
        currentRow?.viDescription ||
        option.name ||
        "",
      departments:
        currentDepartments.length > 0 ? currentDepartments : referenceDepartments,
      category: currentRow?.category || option.type || "",
      estimatedCurrentYear: toAbsoluteInteger(currentRow?.estimatedCurrentYear),
      estimatedPriorYear: toAbsoluteInteger(currentRow?.estimatedPriorYear),
      actualPriorYear: toAbsoluteInteger(currentRow?.actualPriorYear),
      sourceLineItems: Array.isArray(currentRow?.sourceLineItems)
        ? currentRow.sourceLineItems
        : [],
    });
    usedCodes.add(viCode);
  });

  currentRows.forEach((row) => {
    const viCode = getStringValue(row?.viCode);
    if (!viCode || usedCodes.has(viCode)) {
      return;
    }

    mergedRows.push({
      ...row,
      viCode,
      viDescription: row?.viDescription || row?.codeDescription || viCode,
      codeDescription: row?.codeDescription || row?.viDescription || viCode,
      departments: normalizeDepartmentKeys(row?.departments),
      category: row?.category || "",
      estimatedCurrentYear: toAbsoluteInteger(row?.estimatedCurrentYear),
      estimatedPriorYear: toAbsoluteInteger(row?.estimatedPriorYear),
      actualPriorYear: toAbsoluteInteger(row?.actualPriorYear),
      sourceLineItems: Array.isArray(row?.sourceLineItems)
        ? row.sourceLineItems
        : [],
    });
  });

  return mergedRows.sort((firstItem, secondItem) => {
    return `${firstItem.viDescription}`.localeCompare(`${secondItem.viDescription}`);
  });
};

export const buildFallbackBudgetRowsFromActuals = (
  yearFinancials = [],
  vicodeList = [],
) => {
  const referenceMap = buildViOptions(vicodeList).reduce((agg, option) => {
    agg[`${option.code}`] = option;
    return agg;
  }, {});
  const fallbackRows = (Array.isArray(yearFinancials) ? yearFinancials : []).reduce(
    (agg, row) => {
      const viCode = getStringValue(row?.viCode);

      if (!viCode || agg[viCode]) {
        return agg;
      }

      const reference = referenceMap[viCode] ?? null;
      const rowDepartments = normalizeDepartmentKeys(reference?.departments);
      const description =
        reference?.name ||
        getStringValue(row?.viDescription) ||
        getStringValue(row?.codeDescription) ||
        viCode;

      agg[viCode] = {
        viCode,
        viDescription: description,
        codeDescription: description,
        departments: rowDepartments,
        category: reference?.type ?? "",
        estimatedCurrentYear: 0,
        estimatedPriorYear: 0,
        actualPriorYear: 0,
        sourceLineItems: [],
      };

      return agg;
    },
    {},
  );

  return Object.values(fallbackRows).sort((firstItem, secondItem) => {
    return `${firstItem.viDescription}`.localeCompare(`${secondItem.viDescription}`);
  });
};

export const buildEnabledDepartmentsMap = (
  departmentDetails = {},
  budgetRows = [],
) => {
  const existingEnabledDepartments =
    departmentDetails?.enabled_departments &&
    typeof departmentDetails.enabled_departments === "object"
      ? departmentDetails.enabled_departments
      : {};
  const nextEnabledDepartments = { ...existingEnabledDepartments };

  getDepartmentCardsFromRows(budgetRows)
    .filter((item) => item.key !== "executiveSummary")
    .forEach((item) => {
      if (typeof nextEnabledDepartments[item.key] !== "boolean") {
        nextEnabledDepartments[item.key] = true;
      }
    });

  return nextEnabledDepartments;
};

export const applyEnabledDepartmentsToRows = (
  budgetRows = [],
  enabledDepartments = {},
) => {
  if (!Array.isArray(budgetRows)) {
    return [];
  }

  return budgetRows.reduce((agg, row) => {
    const nextDepartments = normalizeDepartmentKeys(row?.departments).filter(
      (departmentKey) => enabledDepartments[departmentKey] !== false,
    );

    if (nextDepartments.length === 0) {
      return agg;
    }

    agg.push({
      ...row,
      departments: nextDepartments,
    });

    return agg;
  }, []);
};

export const getPlannerEmptyStateMessage = (hasAccountingSnapshot = false) => {
  return hasAccountingSnapshot
    ? "Please use Virgilipsas to add data to this year."
    : "Import data to this year.";
};

export const getDepartmentCardsFromRows = (budgetRows = []) => {
  const departmentKeys = new Set();

  (budgetRows ?? []).forEach((row) => {
    normalizeDepartmentKeys(row?.departments).forEach((department) => {
      departmentKeys.add(department);
    });
  });

  return [
    { key: "executiveSummary", name: REPORT_HEADERS.executiveSummary },
    ...Object.keys(DEPARTMENT_LABELS)
      .filter((department) => departmentKeys.has(department))
      .map((department) => ({
        key: department,
        name: DEPARTMENT_LABELS[department],
      })),
  ];
};

export const getPlannerRowsForDepartment = (
  budgetRows = [],
  departmentKey,
  actualOutcomeMap = {},
  previousBudgetMap = {},
) => {
  const rows =
    departmentKey === "executiveSummary"
      ? budgetRows
      : (budgetRows ?? []).filter((row) =>
        normalizeDepartmentKeys(row?.departments).includes(departmentKey),
      );

  return (rows ?? []).map((row) => ({
    viCode: row.viCode,
    title: row.viDescription || row.codeDescription,
    originalBudget:
      previousBudgetMap[`${row.viCode}`] ?? toAbsoluteInteger(row.estimatedPriorYear),
    actualOutcome:
      actualOutcomeMap[`${row.viCode}`] ?? toAbsoluteInteger(row.actualPriorYear),
    estimatedBudget: toAbsoluteInteger(row.estimatedCurrentYear),
    category: row.category,
    isEditable: true,
    departments: normalizeDepartmentKeys(row?.departments),
  }));
};

export const buildLineItemOptions = (rows = []) => {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    value: `${row.viCode}`,
    label: row.title,
    viCode: `${row.viCode}`,
    title: row.title,
    departments: Array.isArray(row.departments) ? row.departments : [],
  }));
};

export const formatAmount = (amount) => {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(Number(amount) || 0));
};

export const getWorkspaceStatus = (workspace = {}) => {
  const budgetRows = workspace?.budget_rows ?? [];
  const importedRows =
    workspace?.bootstrap_metadata?.import_session?.imported_rows ?? [];

  return {
    hasMappedRows: Array.isArray(budgetRows) && budgetRows.length > 0,
    hasImportDraft: Array.isArray(importedRows) && importedRows.length > 0,
  };
};
